<script setup>
import Dialog from "primevue/dialog"; // Import Dialog from PrimeVue
import DatePicker from "primevue/datepicker"; // Import DatePicker for date selection
import { ref, computed, watch } from "vue";
import { useUserStore } from "@/store/user-store";
import AnButton from "../common/AnButton.vue"; // Import AnButton for buttons

// Props and Emits
const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  mode: {
    type: String,
    default: "BUY", // "BUY" or "SELL"
    validator: (value) => ["BUY", "SELL"].includes(value)
  },
  userHoldings: {
    type: Array,
    default: () => []
  },
  preselectedStock: {
    type: Object,
    default: () => null
  },
  selectedStock: {
    type: Object,
    default: () => null
  },
  stockData: {
    type: Object,
    default: () => null
  },
  initialData: {
    type: Object,
    default: () => null
  }
});
const store = useUserStore();
const emit = defineEmits(["update:modelValue", "update:mode"]);

// Watch for dialog open/close to reset fields
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    // Check for pre-filled data from any of the prop sources
    const prefilledData = props.preselectedStock || props.selectedStock || props.stockData || props.initialData;

    if (prefilledData && (prefilledData.stockName || prefilledData.companyName)) {
      // Set the flag to maintain enabled state
      hasPrefilledData.value = true;

      stockName.value = prefilledData.stockName || prefilledData.companyName || "";
      quantity.value = prefilledData.quantity || null;
      price.value = prefilledData.buyPrice || prefilledData.currentPrice || prefilledData.price || null;
      currentPrice.value = prefilledData.currentPrice || prefilledData.latestPrice || null;

      // Set date from prefilledData or current date
      if (prefilledData.transactionDate) {
        date.value = new Date(prefilledData.transactionDate);
      } else {
        date.value = new Date(); // Current date as default
      }

      priceFetching.value = false;
      showDropdown.value = false;
      filteredStocks.value = [];
      useCurrentPrice.value = false;
    } else {
      // Reset all fields when dialog opens (no pre-filled data)
      hasPrefilledData.value = false;
      resetForm();
    }
  } else {
    // Reset the flag when dialog closes
    hasPrefilledData.value = false;
  }
});

// Reactive State
const stockName = ref("");
const quantity = ref(null);
const price = ref(null);
const date = ref(null);
const currentPrice = ref(null);// Current price of selected stock
const priceFetching = ref(false); // Tracks if the price is being fetched
const useCurrentPrice = ref(false); // For SELL mode: use current price or custom price

// Dynamic stock lists based on mode
const stockList = computed(() => {
  if (props.mode === "SELL") {
    return props.userHoldings.map(stock => stock.stockName);
  }
  return ["Reliance Industries","ICICI Bank","ITC","TCS", "Infosys","HDFC Bank","Kotak Mahindra Bank","Bajaj Finserv",'Maruti Suzuki'];
});

const stockIds = computed(() => {
  if (props.mode === "SELL") {
    return props.userHoldings.map(stock => stock.stockId || getStockIdFromName(stock.stockName));
  }
  return [6,5,7,8, 4,1,2,3];
});

const stockSymbols = computed(() => {
  if (props.mode === "SELL") {
    return props.userHoldings.map(stock => stock.stockSymbol || getStockSymbolFromName(stock.stockName));
  }
  return ["RELIANCE","ICICIBANK","ITC","TCS","INFY","HDFCBANK","KOTAKBANK","BAJAJFINSV","MARUTI"];
});

const filteredStocks = ref([]); // Filtered list for AutoComplete
const showDropdown = ref(false); // Show/hide custom dropdown

// Track if we have pre-filled data (for maintaining enabled state)
const hasPrefilledData = ref(false);

// Enable/Disable fields
const isFieldsEnabled = computed(() => {
  // Always enable fields if we have pre-filled data from any prop source
  const prefilledData = props.preselectedStock || props.selectedStock || props.stockData || props.initialData;

  // Check if we currently have pre-filled data OR if we previously had it (maintain enabled state)
  if ((prefilledData && (prefilledData.stockName || prefilledData.companyName)) || hasPrefilledData.value) {
    return true;
  }

  // Original logic for manual stock selection
  return stockList.value.includes(stockName.value);
});

// Helper functions for SELL mode
const getStockIdFromName = (stockName) => {
  const buyModeStocks = ["Reliance Industries","ICICI Bank","ITC","TCS", "Infosys","HDFC Bank","Kotak Mahindra Bank","Bajaj Finserv",'Maruti Suzuki'];
  const buyModeIds = [6,5,7,8, 4,1,2,3,35];
  const index = buyModeStocks.indexOf(stockName);
  return index !== -1 ? buyModeIds[index] : null;
};

const getStockSymbolFromName = (stockName) => {
  const buyModeStocks = ["Reliance Industries","ICICI Bank","ITC","TCS", "Infosys","HDFC Bank","Kotak Mahindra Bank","Bajaj Finserv",'Maruti Suzuki'];
  const buyModeSymbols = ["RELIANCE","ICICIBANK","ITC","TCS","INFY","HDFCBANK","KOTAKBANK","BAJAJFINSV","MARUTI"];
  const index = buyModeStocks.indexOf(stockName);
  return index !== -1 ? buyModeSymbols[index] : null;
};

// Get max quantity user can sell
const maxSellQuantity = computed(() => {
  if (props.mode === "SELL" && stockName.value) {
    const holding = props.userHoldings.find(stock => stock.stockName === stockName.value);
    return holding ? holding.numberOfShares : 0;
  }
  return null;
});

// Close Dialog
const closeDialog = () => {
  emit("update:modelValue", false);
};

// Handle input for custom autocomplete
const handleInput = (event) => {
  const query = event.target.value.toLowerCase();
  if (query.length >= 1) {
    filteredStocks.value = stockList.value
      .filter((stock) => stock.toLowerCase().startsWith(query))
      .sort((a, b) => a.localeCompare(b));
    showDropdown.value = true;
  } else {
    filteredStocks.value = [];
    showDropdown.value = false;
  }
};

// Handle blur event
const handleBlur = () => {
  // Delay hiding dropdown to allow click events
  setTimeout(() => {
    showDropdown.value = false;
  }, 150);
};

// Select stock from dropdown
const selectStock = (stock) => {
  // Check if this is a different stock than currently selected
  const isDifferentStock = stockName.value !== stock;

  stockName.value = stock;
  showDropdown.value = false;

  if (isDifferentStock) {
    // Reset all other fields when stock name changes
    console.log('Stock changed, resetting fields');
    quantity.value = null;
    price.value = null;
    date.value = new Date(); // Set to current date
    currentPrice.value = null;
    useCurrentPrice.value = false;
  }

  fetchCurrentPrice();
};

// Highlight item on hover
const highlightItem = (event) => {
  event.target.style.backgroundColor = '#3b82f6';
  event.target.style.color = 'white';
};

// Remove highlight on mouse leave
const unhighlightItem = (event) => {
  event.target.style.backgroundColor = 'white';
  event.target.style.color = '#374151';
};

// Fetch current price for selected stock
const fetchCurrentPrice = async () => {
    const stockIndex = stockList.value.indexOf(stockName.value);
    if (stockIndex === -1) {
        currentPrice.value = null; // Reset if stock is invalid
        return;
    }

    const stockSymbol = stockSymbols.value[stockIndex];
    priceFetching.value = true; // Start fetching
    try {
        const priceData = await store.getStocksMFCurrentPrice(stockSymbol, "S");
        console.log("Fetched current price data:", priceData);
        currentPrice.value = priceData.stockCurrentDayPrice; // Assume the API returns { currentPrice: <value> }
    } catch (error) {
        console.error("Failed to fetch current price:", error);
        currentPrice.value = null; // Reset on error
    }
    finally {
        priceFetching.value = false; // End fetching
    }
};

// Handle current price checkbox change
const handleCurrentPriceChange = () => {
  console.log("Use current price checkbox changed:", useCurrentPrice.value);
  if (useCurrentPrice.value) {
    // When checkbox is checked, copy current price to sell price input
    price.value = currentPrice.value;
    // Set current date when using current price
    date.value = new Date();
  } else {
    // When checkbox is unchecked, clear the price field and date
    price.value = "";
    date.value = null;
  }
};

// Handle mode switching
const switchMode = (newMode) => {
  emit("update:mode", newMode);
  // Reset form when switching modes
  resetForm();
};

// Reset form function
const resetForm = () => {
  stockName.value = "";
  quantity.value = null;
  price.value = null;
  date.value = null;
  currentPrice.value = null;
  priceFetching.value = false;
  showDropdown.value = false;
  filteredStocks.value = [];
  useCurrentPrice.value = false;
};



// Add Stock Function (supports both BUY and SELL)
const addStock = async () => {
    if (stockName.value && quantity.value && price.value && date.value) {
    let stockIndex = stockList.value.indexOf(stockName.value);
    let stockId = null;

    // Handle prefilled flow - if stock not found in stockList, try to find it in existing holdings
    if (stockIndex === -1 && hasPrefilledData.value) {
      console.log("Prefilled stock not found in stockList, checking existing holdings...");

      // Check if stock exists in user's stocksOwned
      const stocksOwned = store.dashboardData?.stocksOwned;
      if (Array.isArray(stocksOwned)) {
        console.log("All stocksOwned for debugging:", stocksOwned.map(s => ({
          stockName: s.stockName,
          stockSymbol: s.stockSymbol,
          ticker: s.ticker,
          stockId: s.stockId
        })));

        // First try exact name match
        let existingStock = stocksOwned.find(stock => stock.stockName === stockName.value);
        console.log("Exact name match result:", existingStock);

        // If no exact match, try symbol/ticker matching
        if (!existingStock) {
          console.log("No exact name match, trying symbol/ticker matching for stockId...");

          // Create symbol mapping for common stocks
          const symbolToNameMapping = {
            'ICICIBANK': ['ICICI Bank', 'ICICIBANK'],
            'HDFCBANK': ['HDFC Bank', 'HDFCBANK'],
            'RELIANCE': ['Reliance Industries', 'RELIANCE'],
            'TCS': ['TCS', 'Tata Consultancy Services'],
            'INFY': ['Infosys', 'INFY'],
            'ITC': ['ITC', 'ITC Limited'],
            'KOTAKBANK': ['Kotak Mahindra Bank', 'KOTAKBANK'],
            'BAJAJFINSV': ['Bajaj Finserv', 'BAJAJFINSV'],
            'MARUTI': ['Maruti Suzuki', 'MARUTI']
          };

          // Check if current stock matches any known symbols
          const possibleNames = symbolToNameMapping[stockName.value] || [];
          console.log("Possible names for", stockName.value, ":", possibleNames);

          existingStock = stocksOwned.find(stock => {
            console.log("Checking stock:", stock.stockName, "against possibleNames:", possibleNames);
            // Check if portfolio stock name matches any possible names for this symbol
            const matches = possibleNames.some(possibleName => {
              const nameMatch = stock.stockName === possibleName;
              const symbolMatch = stock.stockSymbol === stockName.value;
              const tickerMatch = stock.ticker === stockName.value;
              console.log(`  - "${possibleName}" vs "${stock.stockName}": ${nameMatch}`);
              console.log(`  - symbol "${stockName.value}" vs "${stock.stockSymbol}": ${symbolMatch}`);
              console.log(`  - ticker "${stockName.value}" vs "${stock.ticker}": ${tickerMatch}`);
              return nameMatch || symbolMatch || tickerMatch;
            });
            console.log("Final match result for", stock.stockName, ":", matches);
            return matches;
          });

          console.log("Symbol/ticker matching result:", existingStock);
        }

        if (existingStock) {
          console.log("Found existing stock object:", existingStock);
          console.log("Available properties:", Object.keys(existingStock));

          // Since stocksOwned objects don't have stockId, use symbol-to-ID mapping
          const symbolToIdMapping = {
            'ICICIBANK': 5,
            'HDFCBANK': 1,
            'RELIANCE': 6,
            'TCS': 8,
            'INFY': 4,
            'ITC': 7,
            'KOTAKBANK': 2,
            'BAJAJFINSV': 3,
            'MARUTI': 35
          };

          // Try to get stockId from symbol mapping
          stockId = symbolToIdMapping[existingStock.stockSymbol] || symbolToIdMapping[stockName.value];

          console.log("Found stockId from symbol mapping:", stockId, "for symbol:", existingStock.stockSymbol || stockName.value);

          if (!stockId) {
            console.warn("No stockId found in symbol mapping for:", existingStock.stockSymbol || stockName.value);
          }
        } else {
          console.log("No existing stock found with any matching method");
        }
      }

      // If still no stockId found, generate a fallback ID
      if (!stockId) {
        // Generate a hash-based ID for model portfolio stocks not in predefined list
        const hashId = stockName.value.split('').reduce((a, b) => {
          a = ((a << 5) - a) + b.charCodeAt(0);
          return a & a;
        }, 0);
        stockId = Math.abs(hashId) % 1000 + 2000; // Generate ID between 2000-2999
        console.log("Generated fallback stockId for prefilled stock:", stockId);
      }
    } else {
      // Normal flow - use stockIndex to get stockId
      stockId = stockIds.value[stockIndex];
    }

    console.log(`${props.mode}ing stock with ID:`, stockId, stockName.value, quantity.value, price.value, date.value);

    // Ensure stockId is valid
    if (!stockId) {
      alert("Unable to determine stock ID. Please try again.");
      return;
    }

    // For BUY mode: check if stock exists, for SELL mode: always modify existing
    let transactionType = "A"; // Add new
    if (props.mode === "SELL") {
        transactionType = "S"; // Sell
    } else {
        // Enhanced BUY logic with proper null/undefined check and symbol matching
        const stocksOwned = store.dashboardData?.stocksOwned;
        let exists = false;

        if (Array.isArray(stocksOwned)) {
          // First try exact name match
          exists = stocksOwned.some(stock => stock.stockName === stockName.value);

          // If no exact match and this is prefilled flow, try symbol/ticker matching
          if (!exists && hasPrefilledData.value) {
            console.log("No exact name match, trying symbol/ticker matching for prefilled stock...");

            // Create symbol mapping for common stocks
            const symbolToNameMapping = {
              'ICICIBANK': ['ICICI Bank', 'ICICIBANK'],
              'HDFCBANK': ['HDFC Bank', 'HDFCBANK'],
              'RELIANCE': ['Reliance Industries', 'RELIANCE'],
              'TCS': ['TCS', 'Tata Consultancy Services'],
              'INFY': ['Infosys', 'INFY'],
              'ITC': ['ITC', 'ITC Limited'],
              'KOTAKBANK': ['Kotak Mahindra Bank', 'KOTAKBANK'],
              'BAJAJFINSV': ['Bajaj Finserv', 'BAJAJFINSV'],
              'MARUTI': ['Maruti Suzuki', 'MARUTI']
            };

            // Check if current stock matches any known symbols
            const possibleNames = symbolToNameMapping[stockName.value] || [];
            exists = stocksOwned.some(stock => {
              // Check if portfolio stock name matches any possible names for this symbol
              return possibleNames.some(possibleName =>
                stock.stockName === possibleName ||
                stock.stockSymbol === stockName.value ||
                stock.ticker === stockName.value
              );
            });

            if (exists) {
              console.log(`Found matching stock using symbol mapping: ${stockName.value}`);
            }
          }
        }

        console.log("Stocks already owned:", exists, "stocksOwned:", stocksOwned);
        if (exists) {
            transactionType = "M"; // Modify existing
            console.log("Stock already owned, setting transactionType to 'M'");
        }
    }

    const payload = {
        stockId: stockId,
        stockName: stockName.value,
        numberofShares: parseInt(quantity.value),
        transactionDate: date.value,
        buyOrSellPrice: parseFloat(price.value),
        transactionType: transactionType,
        currentPrice: parseFloat(currentPrice.value),
    };

    try {
            const success = await store.addStock(payload);
            console.log(`Stock ${props.mode.toLowerCase()}ed successfully:`, success);
            if (success) {
                // Close the dialog
                closeDialog();
            }
    } catch (error) {
      console.error(`Error ${props.mode.toLowerCase()}ing stock:`, error);
    }
  } else {
    alert(`Please fill all fields before ${props.mode.toLowerCase()}ing the stock.`);
  }
};
</script>

<template>
  <Dialog
    modal
    :visible="modelValue"
    :closable="false"
    :dismissable-mask="false"
    @update:visible="(value) => emit('update:modelValue', value)"
  >

    <template #header>
      <div class="d-flex align-items-center position-relative w-100 p-20 dialog-header">
        <!-- Mode Toggle Buttons - Left Top Corner -->
        <div class="mode-toggle-container-left">
          <AnButton
            label="Buy"
            :variant="props.mode === 'BUY' ? 'primary' : 'tab'"
            @click="switchMode('BUY')"
            class="mode-toggle-btn-small"
          />
          <AnButton
            label="Sell"
            :variant="props.mode === 'SELL' ? 'primary' : 'tab'"
            @click="switchMode('SELL')"
            class="mode-toggle-btn-small"
          />
        </div>

        <span class="text-center text-primary w-100 font-20 font-weight-700">
          {{ props.mode === 'SELL' ? 'Sell Stock' : 'Add-New or Update-Existing Stock Holding' }}
        </span>
        <i
          @click="closeDialog"
          class="pi pi-times bg-error text-white p-5 br-5 position-absolute r-0 mr-10 cursor-pointer"
        />
      </div>
    </template>


    <div class="ph-30 pb-30">
      <!-- Step 1: Stock Selection -->
      <div class="step-section">
        <div class="step-header">
          <div class="step-indicator">
            <span class="step-number">1</span>
          </div>
          <span class="step-title">Stock Selection</span>
        </div>

        <div class="d-grid stock-grid gap-20 mt-15">
          <div class="d-flex flex-direction-column">
            <label for="stockName" class="font-weight-600">Stock Name</label>
            <div class="custom-autocomplete">
              <input
                id="stockName"
                v-model="stockName"
                @input="handleInput"
                @focus="showDropdown = true"
                @blur="handleBlur"
                placeholder="Enter stock name"
                class="input-field autocomplete-input"
                autocomplete="off"
              />
              <div
                v-if="showDropdown && filteredStocks.length > 0"
                class="custom-dropdown"
              >
                <div
                  v-for="stock in filteredStocks"
                  :key="stock"
                  class="dropdown-item"
                  @mousedown="selectStock(stock)"
                  @mouseenter="highlightItem"
                  @mouseleave="unhighlightItem"
                >
                  {{ stock }}
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex flex-direction-column">
            <label for="currentPrice" class="font-weight-600">Current Price</label>
            <input
              id="currentPrice"
              v-model="currentPrice"
              type="text"
              class="input-field current-price-field"
              placeholder="Current price will be fetched"
              readonly
            />
          </div>
        </div>
      </div>

      <!-- Step 2: Transaction Details -->
      <div class="step-section" :class="{ 'disabled-section': !isFieldsEnabled }">
        <div class="step-header">
          <div class="step-indicator" :class="{ 'active': isFieldsEnabled }">
            <span class="step-number">2</span>
          </div>
          <span class="step-title">Transaction Details</span>
        </div>

        <div class="d-grid transaction-grid gap-20 mt-15">
          <div class="d-flex flex-direction-column">
            <label for="quantity" class="font-weight-600">
              Quantity
              <span v-if="props.mode === 'SELL' && maxSellQuantity !== null" class="text-muted">
                (Max: {{ maxSellQuantity }})
              </span>
            </label>
            <input
              id="quantity"
              v-model="quantity"
              type="text"
              :disabled="!isFieldsEnabled"
              :placeholder="props.mode === 'SELL' ? `Enter quantity (max ${maxSellQuantity || 0})` : 'Enter quantity'"
              class="input-field"
              :class="{ 'border-red-500': props.mode === 'SELL' && quantity > maxSellQuantity }"
              @input="quantity = $event.target.value.replace(/[^0-9]/g, '')"
            />
            <small v-if="props.mode === 'SELL' && quantity > maxSellQuantity" class="error-text">
              Cannot sell more than {{ maxSellQuantity }} shares
            </small>
          </div>

          <div class="d-flex flex-direction-column">
            <label for="price" class="font-weight-600 d-flex align-items-center gap-10">
              {{ props.mode === 'SELL' ? 'Sell Price' : 'Buy Price' }}
              <div v-if="props.mode === 'SELL'" class="d-flex align-items-center gap-5 ml-15">
                <input
                  type="checkbox"
                  id="useCurrentPrice"
                  v-model="useCurrentPrice"
                  @change="handleCurrentPriceChange"
                />
                <label for="useCurrentPrice" class="text-sm font-weight-400">Use current price (₹{{ currentPrice }})</label>
              </div>
            </label>
            <div v-if="props.mode === 'SELL'" class="sell-price-container">
              <input
                id="price"
                v-model="price"
                type="text"
                :disabled="!isFieldsEnabled || useCurrentPrice"
                :placeholder="useCurrentPrice ? `₹${currentPrice}` : 'Enter sell price'"
                class="input-field"
                :class="{ 'disabled-field': useCurrentPrice }"
                @input="price = price.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1')"
              />
            </div>
            <input
              v-else
              id="price"
              v-model="price"
              type="text"
              :disabled="!isFieldsEnabled"
              placeholder="Enter price"
              class="input-field"
              @input="price = price.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1')"
            />
          </div>

          <div class="d-flex flex-direction-column">
            <label for="date" class="font-weight-600">Date</label>
            <DatePicker
              id="date"
              v-model="date"
              :disabled="!isFieldsEnabled || (props.mode === 'SELL' && useCurrentPrice)"
              placeholder="Select date"
              class="input-field"
              :class="{ 'disabled-field': props.mode === 'SELL' && useCurrentPrice }"
              :max-date="new Date()"
              :min-date="new Date('2001-03-01')"
              />
          </div>
        </div>
      </div>
    </div>


    <template #footer>
      <div class="d-flex justify-center gap-20 w-100 p-10">
        <AnButton
          label="Back"
          variant="tab"
          @click="closeDialog"
        />
        <AnButton
          :label="props.mode === 'SELL' ? 'Update' : 'Add'"
          variant="primary"
          :disabled="priceFetching || !isFieldsEnabled"
          @click="addStock"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.dialog-header {
  border-bottom: 2px solid;
  border-bottom-color: var(--an-primary);
}

/* Mode Toggle Styling - Left Top Corner */
.mode-toggle-container-left {
  position: absolute;
  left: 20px;
  top: 20px;
  display: flex;
  gap: 8px;
  z-index: 10;
}

.mode-toggle-btn-small {
  min-width: 60px !important;
  font-size: 12px !important;
  padding: 6px 12px !important;
  height: 32px;
}

/* Step Layout Styling */
.step-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.step-section.disabled-section {
  opacity: 0.6;
  background: #f1f5f9;
}

.step-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.step-indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #cbd5e1;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.step-indicator.active {
  background: var(--an-primary);
}

.step-number {
  font-size: 12px;
}

.step-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

/* Grid Layouts */
.stock-grid {
  grid-template-columns: 1fr 1fr 1fr;
  max-width: 100%;
}

.stock-grid > div:nth-child(2) {
  grid-column: 2;
}

.transaction-grid {
  grid-template-columns: repeat(3, 1fr);
  max-width: 100%;
}

.input-field {
  padding: 16px;
  border: 2px solid var(--an-gray1);
  border-radius: 5px;
  font-size: 14px;
  width: 100%;
  max-width: 280px;
}

.autocomplete-input {
  max-width: 350px;
}

.current-price-field {
  background-color: #f9fafb;
  border-color: #d1d5db;
}

.input-field:focus {
  outline: none;
  border-color: var(--an-primary);
}

/* Dialog Size Styling */
:deep(.p-dialog) {
  width: 30vw !important;
  max-width: 550px !important;
  min-width: 450px !important;
}

:deep(.p-dialog .p-dialog-content) {
  padding: 0 !important;
}

/* Custom AutoComplete Styling */
.custom-autocomplete {
  position: relative;
  width: 100%;
}

.autocomplete-input {
  width: 100%;
}

.custom-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 1050;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  width: 100%;
  max-width: 350px;
}

.dropdown-item {
  padding: 6px 12px;
  font-size: 16px;
  color: #374151;
  cursor: pointer;
  background-color: white;
  transition: all 0.15s ease;
  border-bottom: 1px solid #f3f4f6;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: #3b82f6;
  color: white;
}

/* SELL Mode Styling */
.sell-price-container {
  width: 100%;
}

.disabled-field {
  background-color: #f3f4f6 !important;
  cursor: not-allowed;
}

.error-text {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.border-red-500 {
  border-color: #ef4444 !important;
}

.text-muted {
  color: #6b7280;
  font-size: 12px;
}

.text-sm {
  font-size: 14px;
}

@media (max-width: 700px) {
  :deep(.p-dialog) {
    width: 98vw !important;
    min-width: 0 !important;
    max-width: 100vw !important;
  }
  .ph-30.pb-30 {
    padding-left: 8px !important;
    padding-right: 8px !important;
    padding-bottom: 8px !important;
  }
  .step-section {
    padding: 12px 6px 12px 6px;
    margin-bottom: 12px;
  }
  .d-grid.stock-grid, .d-grid.transaction-grid {
    display: flex !important;
    flex-direction: column !important;
    gap: 12px !important;
  }
  .input-field, .autocomplete-input {
    font-size: 1rem;
    padding: 12px;
    max-width: 100%;
  }
  .dialog-header span {
    font-size: 1.2rem !important;
  }
  .step-title, .font-weight-600 {
    font-size: 1rem !important;
  }
  .d-flex.justify-center.gap-20.w-100.p-10 {
    flex-direction: column;
    gap: 12px;
    padding: 0 0 12px 0;
  }
}
</style>

